import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { PaginationAction, TooltipLabel, StatusChip, Loader, Timestamp } from "components";
import { INVENTORY_TABLE_HEADERS } from "pages/SupplyPanel/constant";
import { useState } from "react";
import { getStatusData } from "../DataModel";
import { formatDate, timeConvertor, validateNull } from "utils";

const TableView = ({ rowsPerPage, onPageChange, data, isLoading }) => {
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const theme = useTheme();

  const ExpandedRowItem = ({ label, value }) => (
    <Grid xs={2} marginBottom={3}>
      <Box>
        <Typography fontSize={12} color={theme?.palette?.grey["A300"]}>
          {label}
        </Typography>
        <Typography fontSize={14} color={theme?.palette?.grey["A300"]}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );

  const TooltipView = ({ row }) => {
    const formattedTooptipColums = [
      {
        label: "Buyer name",
        value: validateNull(row?.buyerSellerDataDTO?.customerName),
      },
      {
        label: "Seller name",
        value: validateNull(row?.buyerSellerDataDTO?.sellerName),
      },
      {
        label: "Buyer GSTIN",
        value: validateNull(row?.buyerSellerDataDTO?.customerGstin),
      },
      {
        label: "Seller GSTIN",
        value: validateNull(row?.buyerSellerDataDTO?.sellerGstin),
      },
    ];

    return (
      <Box>
        {formattedTooptipColums.map((item) => (
          <Box
            key={item?.label}
            display={"flex"}
            gap={6}
            alignItems={"center"}
            mb={2}
            pb={2}
            borderBottom={`1px dashed ${theme?.palette?.grey["A700"]}`}
          >
            <Typography fontSize={12} color={theme?.palette?.grey["A300"]}>
              {item.label}
            </Typography>
            <Typography
              fontWeight={600}
              fontSize={14}
              color={theme?.palette?.grey["A300"]}
            >
              {item.value || "-"}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {INVENTORY_TABLE_HEADERS.map((column) => (
                <TableCell key={column.id}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={INVENTORY_TABLE_HEADERS.length}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              data?.data?.map((row, index) => {
                const status = row?.status;
                return (
                  <>
                    <TableRow
                      key={index + row?.soNumber}
                      onClick={() =>
                        setExpandedRowIndex(
                          expandedRowIndex === index ? null : index,
                        )
                      }
                      hover
                    >
                      <TableCell>{validateNull(row?.inventoryType)}</TableCell>
                      <TableCell>{validateNull(row?.sapSkuName)}</TableCell>
                      <TableCell>{validateNull(row?.soNumber)}</TableCell>
                      <TableCell>
                        {validateNull(row?.soLineItemNumber)}
                      </TableCell>
                      <TableCell>
                        <TooltipLabel
                          title={
                            <Box p={3} width={250}>
                              <TooltipView row={row} />
                            </Box>
                          }
                          labelChildren={
                            <Typography fontWeight={600}>
                              {row?.orderNumber}
                            </Typography>
                          }
                        />
                      </TableCell>
                      <TableCell>{row?.quantity}</TableCell>
                      <TableCell>
                        {row?.expectedFgDate ? (
                          <Timestamp
                            timeStamp={new Date(row.expectedFgDate).toString()}
                            showTime={false}
                          />
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        {status ? (
                          <StatusChip
                            type={getStatusData(status)?.type}
                            label={getStatusData(status)?.label}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {expandedRowIndex === index ? (
                          <ArrowDropUp />
                        ) : (
                          <ArrowDropDown />
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedRowIndex === index && (
                      <TableRow>
                        <TableCell colSpan={INVENTORY_TABLE_HEADERS.length}>
                          <Box ml={10}>
                            <Grid container>
                              <ExpandedRowItem
                                label="ERP SKU name"
                                value={validateNull(row?.erpSkuName)}
                              />
                              <ExpandedRowItem
                                label="Plant code"
                                value={validateNull(row?.plantCode)}
                              />
                              <ExpandedRowItem
                                label="Customer name"
                                value={validateNull(
                                  row?.buyerSellerDataDTO?.customerName,
                                )}
                              />
                              <ExpandedRowItem
                                label="Uploaded at"
                                value={`${formatDate(row?.uploadedAt)} 
                                ${timeConvertor(row?.uploadedAt)}`}
                              />
                              <ExpandedRowItem
                                label="Uploaded by"
                                value={validateNull(row?.uploadedBy)}
                              />
                              <ExpandedRowItem
                                label="Seller name"
                                value={validateNull(row?.buyerSellerDataDTO?.sellerName)}
                              />
                              <ExpandedRowItem
                                label="Buyer GSTIN"
                                value={validateNull(row?.buyerSellerDataDTO?.customerGstin)}
                              />
                              <ExpandedRowItem
                                label="Seller GSTIN"
                                value={validateNull(row?.buyerSellerDataDTO?.sellerGstin)}
                              />
                              <ExpandedRowItem
                                label="Order subtype"
                                value={validateNull(row?.suborderType)}
                              />
                              <ExpandedRowItem
                                label="Shipment status"
                                value={validateNull(row?.shipmentStatus)}
                              />
                              <ExpandedRowItem
                                label="Sold to party"
                                value={validateNull(row?.soldToParty)}
                              />
                              <ExpandedRowItem
                                label="Ship to party"
                                value={validateNull(row?.shipToParty)}
                              />
                            </Grid>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            {!isLoading && !data?.data?.length && (
              <TableRow>
                <TableCell colSpan={INVENTORY_TABLE_HEADERS.length}>
                  <Typography textAlign={"center"}>No data found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[rowsPerPage]}
          rowsPerPage={rowsPerPage}
          count={data?.count ?? 0}
          page={Math.ceil(data?.offset / rowsPerPage) ?? 0}
          onPageChange={onPageChange}
          ActionsComponent={PaginationAction}
          sx={{ display: "flex", flexDirection: "column-reverse" }}
        />
      </TableContainer>
    </Box>
  );
};

export default TableView;
